import React from "react";
import {RgbaColorPicker} from "react-colorful";
import {InputAdornment, TextField} from "@material-ui/core";
import FlexBox from "view/components/Layout/FlexBox";
import {hexValidationPattern} from "tools/validations";
import {DEFAULT_OPACITY, hexToRgba, rgbaToHex} from "tools/theme/helpers";
import useStyles from "./styles";

type ColorPickerProps = {
  color: string;
  setColor: React.Dispatch<React.SetStateAction<string>>;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  opacityValue: string;
  setOpacityValue?: React.Dispatch<React.SetStateAction<string>>;
  updateColor?: (hex: string, opacity: string) => void;
};

const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  setColor,
  inputValue,
  setInputValue,
  opacityValue,
  setOpacityValue,
  updateColor,
}) => {
  const classes = useStyles();

  const handleColorChange = (color: {
    r: number;
    g: number;
    b: number;
    a: number;
  }): void => {
    const hex = rgbaToHex(color.r, color.g, color.b);

    setColor(hex);
    setInputValue(hex);
    setOpacityValue?.(DEFAULT_OPACITY.toString());
  };
  const handleHexInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const {value} = event.target;

    if (hexValidationPattern.test(value)) {
      const hex = `#${value}`;
      setInputValue(hex);

      if (value.length === 6) {
        updateColor?.(hex, opacityValue);
      }
    }
  };
  const handleOpacityInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const {value} = event.target;
    const numericValue = parseInt(value, 10);

    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
      setOpacityValue?.(value);
      updateColor?.(inputValue, value);
    } else if (value === "") {
      setOpacityValue?.("");
    }
  };

  return (
    <FlexBox column gap={2} mb={3}>
      <RgbaColorPicker
        color={hexToRgba(color, DEFAULT_OPACITY)}
        onChange={handleColorChange}
        className={classes.container}
      />
      <FlexBox gap={1}>
        <TextField
          value={inputValue.slice(1)}
          onChange={handleHexInputChange}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                className={classes.hexAdornment}
                disableTypography
              >
                #
              </InputAdornment>
            ),
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{
            maxLength: 6,
            className: classes.hexText,
          }}
          className={classes.hexContainer}
        />

        {!!setOpacityValue && (
          <TextField
            value={opacityValue}
            onChange={handleOpacityInputChange}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  className={classes.percentAdornment}
                  disableTypography
                >
                  %
                </InputAdornment>
              ),
              style: {
                height: 32,
              },
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              maxLength: 3,
              className: classes.percentText,
            }}
            className={classes.percentContainer}
          />
        )}
      </FlexBox>
    </FlexBox>
  );
};

export default ColorPicker;
